import { ApiError } from '@/lib/errors';
import { fetchWithDefaults } from '@/lib/http/client';

// Type definitions
export interface OnboardingStepDetail {
  step_name: string;
  display_name: string;
  status:
    | 'pending'
    | 'in_progress'
    | 'completed'
    | 'failed'
    | 'skipped'
    | 'waiting';
  depends_on: string[];
  blocked_by: string[];
  started_at: string | null;
  completed_at: string | null;
  error_message: string;
  retry_count: number;
  can_retry: boolean;
  can_skip: boolean;
}

/**
 * Status of a connected email or calendar integration.
 *
 * Semantic, provider-agnostic: ``provider`` carries the registry name
 * (e.g. ``"gmail"``, ``"testemail"``, ``"gcal"``, ``"testcalendar"``) so UIs
 * can branch on the specific vendor for icons / labels, while ``connected``
 * reflects whether ANY provider of this kind is connected to the org.
 */
export interface IntegrationStatus {
  connected: boolean;
  provider: string | null;
  email: string | null;
}

export interface OnboardingStatus {
  onboarding: {
    is_completed: boolean;
    current_step: string;
    skipped_steps: string[];
    completed_at: string | null;
  };
  services: {
    email: IntegrationStatus;
    calendar: IntegrationStatus;
  };
  enrichment_pending: boolean | null;
  steps: OnboardingStepDetail[];
  pipeline_complete: boolean;
  steps_completed: number;
  steps_total: number;
  orphaned_step_count: number;
}

export interface TeamSuggestion {
  email: string;
  name: string;
  score: number;
  domain_match: boolean;
  email_interactions: number;
  meeting_count: number;
  total_interactions: number;
}

export interface TeamSuggestionsResponse {
  suggestions: TeamSuggestion[];
  total_count: number;
}

export interface InvitationsResponse {
  created: Array<{
    email: string;
    invitation_id: string;
    status: string;
  }>;
  existing: Array<{
    email: string;
    invitation_id: string;
    status: string;
  }>;
  errors: Array<{
    email: string;
    error: string;
  }>;
  total_sent: number;
}

export interface RetryStepResponse {
  step_name: string;
  status: string;
  retry_count: number;
  error_message: string;
}

export interface SkipStepResponse {
  step_name: string;
  status: string;
  error_message: string;
}

class OnboardingService {
  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorData}`);
    }
    return response.json();
  }

  /**
   * Get onboarding status for a team (calls pipeline orchestrator endpoint)
   */
  async getStatus(orgId: number): Promise<OnboardingStatus> {
    const response = await fetchWithDefaults(`/a/${orgId}/onboarding/status/`);
    if (!response.ok) {
      throw new ApiError(
        `Onboarding status failed: ${response.status}`,
        response.status
      );
    }
    return response.json() as Promise<OnboardingStatus>;
  }

  /**
   * Retry a failed onboarding step
   */
  async retryStep(orgId: number, stepName: string): Promise<RetryStepResponse> {
    const response = await fetchWithDefaults(
      `/a/${orgId}/onboarding/steps/${stepName}/retry/`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    return this.handleResponse(response);
  }

  /**
   * Skip a failed onboarding step
   */
  async skipStep(orgId: number, stepName: string): Promise<SkipStepResponse> {
    const response = await fetchWithDefaults(
      `/a/${orgId}/onboarding/steps/${stepName}/skip/`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return this.handleResponse(response);
  }

  /**
   * Update onboarding step status
   * TODO: Backend endpoint not yet implemented - this is a placeholder
   */
  async updateStep(_orgId: number, _step: string): Promise<{ status: string }> {
    // No-op placeholder until backend endpoint is implemented
    return Promise.resolve({ status: 'ok' });
  }

  /**
   * Trigger writing style analysis
   */
  async triggerWritingAnalysis(orgId: number): Promise<{
    task_ids: Record<string, string>;
    status: string;
    demo_mode?: boolean;
  }> {
    const response = await fetchWithDefaults(
      `/api/orgs/${orgId}/onboarding/trigger-analysis/`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return this.handleResponse(response);
  }

  /**
   * Get team member suggestions
   */
  async getTeamSuggestions(
    orgId: number,
    params?: {
      days?: number;
      min_interactions?: number;
      max_suggestions?: number;
    }
  ): Promise<TeamSuggestionsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.days !== undefined) {
      queryParams.append('days', params.days.toString());
    }
    if (params?.min_interactions !== undefined) {
      queryParams.append(
        'min_interactions',
        params.min_interactions.toString()
      );
    }
    if (params?.max_suggestions !== undefined) {
      queryParams.append('max_suggestions', params.max_suggestions.toString());
    }

    const url = `/api/orgs/${orgId}/onboarding/team-suggestions/${
      queryParams.toString() ? `?${queryParams.toString()}` : ''
    }`;

    const response = await fetchWithDefaults(url);

    return this.handleResponse<TeamSuggestionsResponse>(response);
  }

  /**
   * Send team invitations
   */
  async sendInvitations(
    orgId: number,
    emails: string[]
  ): Promise<InvitationsResponse> {
    const response = await fetchWithDefaults(
      `/api/orgs/${orgId}/onboarding/send-invitations/`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ emails }),
      }
    );

    return this.handleResponse<InvitationsResponse>(response);
  }

  /**
   * Setup demo data for team (demo mode only)
   */
  async setupDemoData(
    orgId: number
  ): Promise<{ status: string; message: string }> {
    const response = await fetchWithDefaults(
      `/api/orgs/${orgId}/onboarding/demo-data/`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return this.handleResponse(response);
  }
}

export const onboardingService = new OnboardingService();

export default onboardingService;
